import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Lock, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { SERVICES_DROPDOWN } from "../../data/servicesData";
import emailjs from "@emailjs/browser";

// EmailJS Configuration from environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_7ulzw5a";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_qzjbmgv";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "6AwoN92OuqrpvhI7H";

// Generate time slots
const TIME_SLOTS = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM",
  "05:00 PM", "05:30 PM", "06:00 PM"
];

export function AppointmentModal({ open, onClose, accent = "#dc2626" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    service_interested: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_interested) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      // Send email via EmailJS
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        company_name: form.company_name || "N/A",
        service_interested: form.service_interested,
        preferred_date: form.preferred_date || "Not specified",
        preferred_time: form.preferred_time || "Not specified",
        message: form.message || "No additional message",
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitted(true);
      toast.success("Appointment request sent successfully!");
    } catch (error) {
      console.error("EmailJS Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setForm({ 
      name: "", email: "", phone: "", company_name: "", 
      service_interested: "", preferred_date: "", preferred_time: "", message: "" 
    });
    onClose();
  };

  // Get min date (today)
  const today = new Date().toISOString().split('T')[0];

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
        data-testid="appointment-modal-overlay"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

        {/* Modal - Fixed height with scroll for mobile */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.96 }}
          transition={{ duration: 0.3 }}
          className="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] sm:max-h-[85vh] flex flex-col"
          data-testid="appointment-modal"
        >
          {/* Header */}
          <div
            className="p-6 pb-4 relative flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
          >
            <button
              data-testid="appointment-modal-close"
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors z-10"
            >
              <X size={16} className="text-white" />
            </button>
            <h2 className="font-heading text-2xl font-black text-white">
              Book Your Appointment
            </h2>
            <p className="text-white/70 text-sm font-body mt-1">
              Fill the form — we'll get back within 24 hours
            </p>
          </div>

          {/* Scrollable Content Area */}
          <div className="overflow-y-auto flex-1">
            {submitted ? (
              <div className="p-8 text-center">
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ background: `${accent}12` }}
                >
                  <Send size={28} style={{ color: accent }} />
                </div>
                <h3 className="font-heading text-xl font-black text-slate-900 mb-2">
                  Thank you!
                </h3>
                <p className="text-slate-500 text-sm font-body mb-6">
                  Our team will contact you shortly.
                </p>
                <button
                  data-testid="appointment-modal-done"
                  onClick={handleClose}
                  className="font-extrabold text-sm px-8 py-3 rounded-full text-white transition-all active:scale-95 font-body"
                  style={{ background: accent }}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="appointment-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "Name", key: "name", placeholder: "Your Name", type: "text" },
                    { label: "Email", key: "email", placeholder: "you@company.com", type: "email" },
                    { label: "Phone", key: "phone", placeholder: "+91 XXXXX XXXXX", type: "tel" },
                    { label: "Company", key: "company_name", placeholder: "Company Name", type: "text", required: false },
                  ].map(({ label, key, placeholder, type, required = true }) => (
                    <div key={key}>
                      <label className="block text-[12px] font-bold text-slate-600 mb-1.5 font-body">
                        {label} {required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        data-testid={`appointment-${key}`}
                        type={type}
                        placeholder={placeholder}
                        value={form[key]}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 outline-none font-body transition-colors focus:border-red-400"
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-slate-600 mb-1.5 font-body">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="appointment-service"
                    value={form.service_interested}
                    onChange={(e) => handleChange("service_interested", e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 outline-none font-body appearance-none focus:border-red-400"
                  >
                    <option value="">Select a service...</option>
                    {SERVICES_DROPDOWN.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Date & Time Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Preferred Date */}
                  <div>
                    <label className="block text-[12px] font-bold text-slate-600 mb-1.5 font-body flex items-center gap-1.5">
                      <Calendar size={12} className="text-slate-400" />
                      Preferred Date
                    </label>
                    <input
                      data-testid="appointment-date"
                      type="date"
                      min={today}
                      value={form.preferred_date}
                      onChange={(e) => handleChange("preferred_date", e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 outline-none font-body transition-colors focus:border-red-400"
                    />
                  </div>

                  {/* Preferred Time */}
                  <div>
                    <label className="block text-[12px] font-bold text-slate-600 mb-1.5 font-body flex items-center gap-1.5">
                      <Clock size={12} className="text-slate-400" />
                      Preferred Time
                    </label>
                    <select
                      data-testid="appointment-time"
                      value={form.preferred_time}
                      onChange={(e) => handleChange("preferred_time", e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 outline-none font-body appearance-none focus:border-red-400"
                    >
                      <option value="">Select time...</option>
                      {TIME_SLOTS.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-slate-600 mb-1.5 font-body">
                    Message
                  </label>
                  <textarea
                    data-testid="appointment-message"
                    rows={3}
                    placeholder="Tell us about your requirements..."
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 outline-none font-body resize-none focus:border-red-400"
                  />
                </div>

                <button
                  data-testid="appointment-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-extrabold text-sm py-3.5 rounded-2xl transition-all active:scale-[0.98] font-body disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
                    boxShadow: `0 8px 30px ${accent}30`,
                  }}
                >
                  {loading ? "Submitting..." : "Book Appointment"}
                  {!loading && <Send size={14} />}
                </button>

                <p className="text-center text-slate-400 text-xs flex items-center justify-center gap-1.5 font-body">
                  <Lock size={11} /> Your data is 100% secure.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
